import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const RecurringEventCard = ({ schedule, onEdit, onToggle, onDelete, onGenerateNow }) => {
  const handleDelete = () => {
    const confirm = Platform.OS === 'web'
      ? window.confirm(`Delete "${schedule.title}"? Future events won't be auto-created.`)
      : null;
    if (Platform.OS === 'web') {
      if (confirm) onDelete(schedule.id);
      return;
    }
    Alert.alert(
      'Delete Schedule',
      `Delete "${schedule.title}"? Future events won't be auto-created.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => onDelete(schedule.id) },
      ]
    );
  };

  return (
    <View style={[styles.card, !schedule.is_active && styles.cardInactive]}>
      <View style={styles.cardHeader}>
        <View style={styles.titleRow}>
          <Text style={[styles.title, !schedule.is_active && styles.titleInactive]}>
            {schedule.title}
          </Text>
          <View style={[styles.typeBadge, !schedule.is_active && styles.typeBadgeInactive]}>
            <Text style={styles.typeBadgeText}>{schedule.event_type}</Text>
          </View>
        </View>
        {!schedule.is_active && (
          <View style={styles.pausedBadge}>
            <Icon name="pause-circle-filled" size={14} color="#FF9800" />
            <Text style={styles.pausedBadgeText}>Paused</Text>
          </View>
        )}
      </View>

      <View style={styles.detailsRow}>
        <Icon name="today" size={16} color="#666" />
        <Text style={styles.detailText}>
          Every {DAYS[schedule.day_of_week]}
        </Text>
      </View>

      <View style={styles.detailsRow}>
        <Icon name="schedule" size={16} color="#666" />
        <Text style={styles.detailText}>
          {formatTime12(schedule.start_time)}
          {schedule.end_time ? ` - ${formatTime12(schedule.end_time)}` : ''}
        </Text>
      </View>

      {(schedule.location_name || schedule.location_address) && (
        <View style={styles.detailsRow}>
          <Icon name="location-on" size={16} color="#666" />
          <Text style={styles.detailText} numberOfLines={1}>
            {schedule.location_name || schedule.location_address}
          </Text>
        </View>
      )}

      {schedule.last_generated_for_date && (
        <View style={styles.detailsRow}>
          <Icon name="autorenew" size={16} color="#999" />
          <Text style={[styles.detailText, { color: '#999', fontSize: 12 }]}>
            Last generated: {new Date(schedule.last_generated_for_date).toLocaleDateString()}
          </Text>
        </View>
      )}

      <View style={styles.actions}>
        <TouchableOpacity style={styles.actionButton} onPress={() => onEdit(schedule)}>
          <Icon name="edit" size={18} color="#333" />
          <Text style={styles.actionText}>Edit</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => onToggle(schedule.id, !schedule.is_active)}
        >
          <Icon
            name={schedule.is_active ? 'pause' : 'play-arrow'}
            size={18}
            color={schedule.is_active ? '#FF9800' : '#4CAF50'}
          />
          <Text style={[styles.actionText, { color: schedule.is_active ? '#FF9800' : '#4CAF50' }]}>
            {schedule.is_active ? 'Pause' : 'Resume'}
          </Text>
        </TouchableOpacity>

        {onGenerateNow && schedule.is_active && (
          <TouchableOpacity style={styles.actionButton} onPress={() => onGenerateNow(schedule)}>
            <Icon name="flash-on" size={18} color="#1976D2" />
            <Text style={[styles.actionText, { color: '#1976D2' }]}>Generate</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity style={styles.actionButton} onPress={handleDelete}>
          <Icon name="delete-outline" size={18} color="#F44336" />
          <Text style={[styles.actionText, { color: '#F44336' }]}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderLeftWidth: 4,
    borderLeftColor: '#94EB98',
  },
  cardInactive: {
    opacity: 0.7,
    borderLeftColor: '#ccc',
  },
  cardHeader: {
    marginBottom: 10,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
    color: '#333',
    flex: 1,
    marginRight: 8,
  },
  titleInactive: {
    color: '#999',
  },
  typeBadge: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 10,
  },
  typeBadgeInactive: {
    backgroundColor: '#e0e0e0',
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
  },
  pausedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  pausedBadgeText: {
    fontSize: 12,
    color: '#FF9800',
    fontWeight: '500',
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  detailText: {
    fontSize: 14,
    color: '#555',
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    gap: 4,
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#333',
  },
});

export default RecurringEventCard;
